import { useState } from 'react';
// @ts-ignore
import { EditableSlotGroup } from '@vibesites/runtime/react';
// @ts-ignore
import type { SlotGroupDefinition } from '@vibesites/runtime';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus } from '@fortawesome/free-solid-svg-icons';

export interface FAQDropdownItem {
  groupPath: string;
  definition: SlotGroupDefinition;
  question: string;
  answer: string;
}

export interface FAQDropdownProps {
  items: FAQDropdownItem[];
  heading?: React.ReactNode;
  variant?: 'white' | 'card';
  standalone?: boolean;
  /** When true, shows outer card border. Default true for card variant, false for standalone. */
  showBorder?: boolean;
}

/**
 * Reusable FAQ accordion component. Accepts n slot group entries with pre-resolved
 * question/answer content. Parent handles {property_name} replacement.
 */
export function FAQDropdown({
  items,
  heading,
  variant = 'white',
  standalone = false,
  showBorder = variant === 'card',
}: FAQDropdownProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const activeItems = items.filter(item => item.question);
  if (activeItems.length === 0) return null;

  const cardClass = showBorder ? 'prop-contact-card prop-contact-faq-card' : 'prop-contact-faq-card-wrap';
  const content = (
    <div className={cardClass}>
      {heading != null && (
        <>
          {typeof heading === 'string' ? <h3 className="prop-contact-faq-heading">{heading}</h3> : heading}
          <div className="prop-al-separator" />
        </>
      )}
      <div className="prop-contact-faq-list">
        {items.map((item, index) =>
          item.question ? (
            <EditableSlotGroup key={item.groupPath} groupPath={item.groupPath} definition={item.definition}>
              <div className={`prop-contact-faq-item${openIndex === index ? ' open' : ''}`}>
                <button
                  type="button"
                  className="prop-contact-faq-question"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                >
                  <span dangerouslySetInnerHTML={{ __html: item.question }} />
                  <span className="prop-contact-faq-toggle">
                    <FontAwesomeIcon icon={openIndex === index ? faMinus : faPlus} />
                  </span>
                </button>
                <div className="prop-contact-faq-answer-wrapper">
                  <div className="prop-contact-faq-answer" dangerouslySetInnerHTML={{ __html: item.answer }} />
                </div>
              </div>
            </EditableSlotGroup>
          ) : null
        )}
      </div>
    </div>
  );

  if (!standalone) return content;

  const sectionClass =
    variant === 'white' ? 'prop-content-section prop-content-section-white' : 'prop-content-section prop-content-section-dark';

  return (
    <section className={sectionClass}>
      <div className="prop-container">{content}</div>
    </section>
  );
}

export default FAQDropdown;
